import React from 'react';
import { Crew } from '@models/crew.model';
import { Avatar } from '@components/atoms/Avatar';
import { useTranslation } from '@hooks/useTranslation';

interface SelectedCrewMembersProps {
  crews: Crew[];
}

/**
 * Renders the member list of each selected crew, grouped under the crew name.
 * Shown immediately under the "Assign to Crews" dropdown so the assigner can
 * see who is on each crew without opening anything. Members already arrive on
 * the crew object (`crew.employees[].user`), so this is purely presentational.
 */
export const SelectedCrewMembers: React.FC<SelectedCrewMembersProps> = ({
  crews,
}) => {
  const tTaskDetail = useTranslation('taskDetail');
  const crewsWithId = crews.filter((crew) => crew.crewId);
  if (crewsWithId.length === 0) return null;

  return (
    <div className="mt-4 space-y-4">
      {crewsWithId.map((crew) => {
        const members = crew.employees ?? [];
        return (
          <div
            key={crew.crewId}
            className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="mb-2 flex items-center justify-between border-b border-gray-200 pb-2">
              <span className="text-sm font-semibold text-gray-800">
                {crew.name}
              </span>
              <span className="text-xs text-gray-500">
                {members.length}{' '}
                {members.length === 1 ? tTaskDetail.memberSingular : tTaskDetail.memberPlural}
              </span>
            </div>

            {members.length === 0 ? (
              <p className="text-xs italic text-gray-400">
                {tTaskDetail.noMembersAssigned}
              </p>
            ) : (
              <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {members.map((employee) => {
                  const user = employee.user;
                  const fullName =
                    `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() ||
                    tTaskDetail.unknownUser;
                  return (
                    <li
                      key={employee.employeeId}
                      className="flex min-w-0 items-center gap-2">
                      <Avatar
                        size="sm"
                        src={user?.avatar}
                        text={fullName}
                        alt={fullName}
                      />
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate text-sm font-medium text-gray-900">
                          {fullName}
                        </span>
                        {user?.email && (
                          <span className="truncate text-xs text-gray-500">
                            {user.email}
                          </span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
};
